import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import AdminLayout from './components/layout/AdminLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import LoadingFallback from './components/ui/LoadingFallback'
import { useAuthStore } from './stores/authStore'

// Eager load: pages critiques pour le premier rendu
import LoginPage from './pages/auth/LoginPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import ProfilePage from './pages/auth/ProfilePage'

// Lazy load: toutes les autres pages
const RidesPage = lazy(() => import('./pages/rides/RidesPage'))
const RideDetailPage = lazy(() => import('./pages/rides/RideDetailPage'))
const DriversPage = lazy(() => import('./pages/drivers/DriversPage'))
const DriverDetailPage = lazy(() => import('./pages/drivers/DriverDetailPage'))
const UsersPage = lazy(() => import('./pages/users/UsersPage'))
const UserDetailPage = lazy(() => import('./pages/users/UserDetailPage'))
const StaffPage = lazy(() => import('./pages/staff/StaffPage'))
const FinancePage = lazy(() => import('./pages/finance/FinancePage'))
const TariffsPage = lazy(() => import('./pages/settings/TariffsPage'))
const RolesPage = lazy(() => import('./pages/settings/RolesPage'))
const PermissionsPage = lazy(() => import('./pages/settings/PermissionsPage'))
const PromoCodesPage = lazy(() => import('./pages/promo/PromoCodesPage'))
const FlashInfosPage = lazy(() => import('./pages/flash-infos/FlashInfosPage'))
const VehicleTypesPage = lazy(() => import('./pages/vehicle-types/VehicleTypesPage'))
const ClaimsPage = lazy(() => import('./pages/claims/ClaimsPage'))
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'))
const ZonesPage = lazy(() => import('./pages/zones/ZonesPage'))
const SubscriptionsPage = lazy(() => import('./pages/subscriptions/SubscriptionsPage'))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'))
const AuditLogsPage = lazy(() => import('./pages/audit/AuditLogsPage'))

/**
 * Route d'accueil (`/`) : le dashboard n'est rendu que si l'utilisateur
 * possède la permission `dashboard.view`. Sinon, redirection vers sa
 * première page accessible (homePath) — les requêtes du dashboard ne
 * sont jamais exécutées pour un utilisateur non autorisé.
 */
function DashboardIndex() {
  const hasPermission = useAuthStore((state) => state.hasPermission)
  const homePath = useAuthStore((state) => state.homePath)

  if (hasPermission('dashboard.view')) {
    return <DashboardPage />
  }
  return <Navigate to={homePath()} replace />
}

export default function App() {
  return (
    <Suspense fallback={<LoadingFallback fullScreen />}>
      <Routes>
        {/* Public route */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard - eager loaded, rendu uniquement si l'utilisateur
              possède `dashboard.view` (sinon : redirection vers sa première
              page accessible — évite d'exécuter toutes les requêtes du
              dashboard qui seraient refusées par le backend). */}
          <Route index element={<DashboardIndex />} />

          {/* Profile */}
          <Route path="profile" element={<ProfilePage />} />
          
          {/* Rides */}
          <Route path="rides" element={<ProtectedRoute permission="rides.view"><RidesPage /></ProtectedRoute>} />
          <Route path="rides/:id" element={<ProtectedRoute permission="rides.view"><RideDetailPage /></ProtectedRoute>} />
          
          {/* Drivers */}
          <Route path="drivers" element={<ProtectedRoute permission="drivers.view"><DriversPage /></ProtectedRoute>} />
          <Route path="drivers/:id" element={<ProtectedRoute permission="drivers.view"><DriverDetailPage /></ProtectedRoute>} />
          
          {/* Users/Clients */}
          <Route path="users" element={<ProtectedRoute permission="users.view" excludeRoles={['superadmin']}><UsersPage /></ProtectedRoute>} />
          <Route path="users/:id" element={<ProtectedRoute permission="users.view_details"><UserDetailPage /></ProtectedRoute>} />
          
          {/* Staff/Admin Users */}
          <Route 
            path="staff" 
            element={
              <ProtectedRoute permission="users.view">
                <StaffPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Finance */}
          <Route path="finance" element={<ProtectedRoute permission="finance.view"><FinancePage /></ProtectedRoute>} />
          
          {/* Settings & Configuration */}
          <Route path="tariffs" element={<ProtectedRoute permission="tariffs.view"><TariffsPage /></ProtectedRoute>} />
          {/* Catalogue dynamique : mêmes slugs que les tarifs, les profils
              chauffeurs et l'écran de réservation. */}
          <Route path="vehicle-types" element={<ProtectedRoute permission="vehicle_types.view"><VehicleTypesPage /></ProtectedRoute>} />
          <Route path="zones" element={<ProtectedRoute permission="zones.view"><ZonesPage /></ProtectedRoute>} />
          <Route path="subscriptions" element={<ProtectedRoute permission="subscriptions.view"><SubscriptionsPage /></ProtectedRoute>} />
          <Route path="promos" element={<ProtectedRoute permission="promos.view"><PromoCodesPage /></ProtectedRoute>} />
          <Route path="flash-infos" element={<ProtectedRoute permission="flash_infos.view"><FlashInfosPage /></ProtectedRoute>} />
          
          {/* Support */}
          <Route path="claims" element={<ProtectedRoute permission="claims.view"><ClaimsPage /></ProtectedRoute>} />
          
          {/* Reports & Analytics */}
          <Route path="reports" element={<ProtectedRoute permission="reports.view"><ReportsPage /></ProtectedRoute>} />
          
          {/* System */}
          <Route path="settings" element={<ProtectedRoute roles={['admin', 'superadmin']}><SettingsPage /></ProtectedRoute>} />
          <Route path="notifications" element={<ProtectedRoute permission="notifications.view"><NotificationsPage /></ProtectedRoute>} />
          <Route 
            path="audit" 
            element={
              <ProtectedRoute permission="audit.view">
                <AuditLogsPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Rôles & Permissions */}
          <Route 
            path="roles" 
            element={
              <ProtectedRoute permission="roles.view">
                <RolesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="permissions" 
            element={
              <ProtectedRoute permission="permissions.view">
                <PermissionsPage />
              </ProtectedRoute>
            } 
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
